import _ from 'lodash';
import numeral from 'numeral';
import 'chartjs-plugin-datalabels';
import React from 'react';
import { Card, Row, Col } from 'grab-ui';
import { Bar } from '@reactchartjs/react-chart.js';
import {
  abbreviateNumber,
  numberWithCommas,
} from 'omega-rtc/utils/metric-utils';
import { calculateYLabelGap } from 'omega-rtc/views/trident-campaigns/metrics/utils';
import {
  CAMPAIGN_METRICS_CHART_CONFIG,
  CAMPAIGN_METRICS_FORMULA_PROPS,
} from 'omega-rtc/constants/ucd';

import './CampaignDetailMetricsCard.scss';

const pushLabels = ['triggered', 'sent', 'notsent', 'delivered', 'open'];
const pciLabels = ['triggered', 'click'];

const CampaignCommsMetricsCard = ({ name, metrics }) => {
  const isPCI = ['pci', 'pushinbox', 'push chained inbox'].includes(
    _.lowerCase(name),
  );
  const labels = [];
  const data = [];
  const backgroundColors = [];
  const isPush = name === 'push';

  if (isPush) {
    pushLabels.forEach((label) => {
      data.push(metrics[label]);
      labels.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(label)}.text`,
          label,
        ),
      );
      backgroundColors.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(label)}.bg`,
          '#00B14F',
        ),
      );
    });
  } else if (isPCI) {
    pciLabels.forEach((label) => {
      data.push(metrics[label]);
      labels.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(label)}.text`,
          label,
        ),
      );
      backgroundColors.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(label)}.bg`,
          '#00B14F',
        ),
      );
    });
  } else {
    // chart data
    _.forEach(metrics, (eventData, eventName) => {
      if (eventName === 'rates') return;
      data.push(eventData);
      labels.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(eventName)}.text`,
          eventName,
        ),
      );
      backgroundColors.push(
        _.get(
          CAMPAIGN_METRICS_CHART_CONFIG,
          `${_.toLower(name)}.metrics.${_.toLower(eventName)}.bg`,
          '#00B14F',
        ),
      );
    });
  }

  const yLabelGap = calculateYLabelGap(data, 10, 4);
  const chartData = {
    labels,
    datasets: [
      {
        label: '',
        data,
        borderWidth: 0,
        backgroundColor: backgroundColors,
      },
    ],
  };
  const options = {
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            stepSize: yLabelGap,
            callback(label) {
              return abbreviateNumber(Number(label));
            },
            padding: 16,
          },
          gridLines: {
            drawBorder: false,
            color: '#F0EFEF',
            zeroLineColor: '#F0EFEF',
          },
        },
      ],
      xAxes: [
        {
          maxBarThickness: 16,
          gridLines: {
            display: false,
          },
          ticks: {
            padding: 16,
          },
        },
      ],
    },
    legend: {
      display: false,
    },
    layout: {
      padding: {
        top: 20,
      },
    },
    tooltips: {
      callbacks: {
        label(tooltipItem, data) {
          return numberWithCommas(data.datasets[0].data[tooltipItem.index]);
        },
      },
    },
    hover: {
      animationDuration: 0,
    },
    plugins: {
      datalabels: {
        // for showing labels on top of each bar
        anchor: 'end',
        align: 'top',
        formatter(value) {
          return abbreviateNumber(value);
        },
      },
    },
  };
  const notifClickRate =
    metrics.rates[CAMPAIGN_METRICS_FORMULA_PROPS.NOTIF_CLICK_RATE];
  const openRate = metrics.rates[CAMPAIGN_METRICS_FORMULA_PROPS.OPEN_RATE];
  const ctaClickRate =
    metrics.rates[CAMPAIGN_METRICS_FORMULA_PROPS.CTA_CLICK_RATE];

  return (
    <Card className="campaign-metrics-card" title={_.capitalize(name)}>
      <Row style={{ marginTop: 0 }}>
        {!isNaN(notifClickRate) && (
          <Col span={8}>
            <p className="text-muted">
              {
                CAMPAIGN_METRICS_CHART_CONFIG[name].rates[
                  CAMPAIGN_METRICS_FORMULA_PROPS.NOTIF_CLICK_RATE
                ].text
              }
            </p>
            <p className="text-percentage">
              {numeral(notifClickRate).format('0.00 %')}
            </p>
          </Col>
        )}
        {!isNaN(openRate) && (
          <Col span={8}>
            <p className="text-muted">
              {
                CAMPAIGN_METRICS_CHART_CONFIG[name].rates[
                  CAMPAIGN_METRICS_FORMULA_PROPS.OPEN_RATE
                ].text
              }
            </p>
            <p className="text-percentage">
              {numeral(openRate).format('0.00 %')}
            </p>
          </Col>
        )}
        {!isNaN(ctaClickRate) && <Col span={8} />}
        {!isNaN(ctaClickRate) && (
          <Col span={8}>
            <p className="text-muted">
              {
                CAMPAIGN_METRICS_CHART_CONFIG[name].rates[
                  CAMPAIGN_METRICS_FORMULA_PROPS.CTA_CLICK_RATE
                ].text
              }
            </p>
            <p className="text-percentage">
              {numeral(ctaClickRate).format('0.00 %')}
            </p>
          </Col>
        )}
      </Row>
      <Row>
        <Bar data={chartData} options={options} />
      </Row>
      <Row style={{ height: 24 }}>
        {isPCI && (
          <div>
            <div className="note">
              <span /> <span>Notification</span>
            </div>
            <div className="note">
              <span style={{ background: '#B1EABA' }} /> <span>Inbox</span>
            </div>
          </div>
        )}
      </Row>
    </Card>
  );
};

export default CampaignCommsMetricsCard;
